import { useCallback, useEffect, useRef } from 'react';

const IDLE_INTERVAL_MS = 30000;
const ACTIVE_INTERVAL_MS = 2000;
const MAX_ACTIVE_DURATION_MS = 5 * 60 * 1000;

interface UseConfigAutoRefreshOptions {
  // Called with silent=true for every background tick (idle 30s or active 2s)
  // so callers can suppress their own loading-flag dispatch and avoid
  // flashing a loader for a refresh the operator didn't ask for. Called with
  // silent=false exactly once - the very first fetch on mount/enable ("first
  // load of screen") - so that one is allowed to show its normal loader.
  fetchFns: Array<(silent: boolean) => void>;
  enabled?: boolean;
  idleIntervalMs?: number;
  activeIntervalMs?: number;
  maxActiveDurationMs?: number;
}

/**
 * Shared polling behavior for the Configuration dialog:
 * - fetches immediately whenever enabled (mount / tab switch / dialog open)
 * - polls every 30s at idle
 * - startAction() switches to a 2s poll; stopAction() (or a safety ceiling if
 *   the caller never detects completion) reverts to the 30s idle poll
 */
export function useConfigAutoRefresh({
  fetchFns,
  enabled = true,
  idleIntervalMs = IDLE_INTERVAL_MS,
  activeIntervalMs = ACTIVE_INTERVAL_MS,
  maxActiveDurationMs = MAX_ACTIVE_DURATION_MS,
}: UseConfigAutoRefreshOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeDeadlineRef = useRef<number | null>(null);
  const fetchFnsRef = useRef(fetchFns);
  fetchFnsRef.current = fetchFns;
  const hasFetchedOnceRef = useRef(false);

  const runFetches = useCallback(() => {
    const silent = hasFetchedOnceRef.current;
    hasFetchedOnceRef.current = true;
    fetchFnsRef.current.forEach((fn) => fn(silent));
  }, []);

  const clearPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const schedule = useCallback((ms: number) => {
    clearPolling();
    intervalRef.current = setInterval(runFetches, ms);
  }, [clearPolling, runFetches]);

  const startIdlePolling = useCallback(() => {
    activeDeadlineRef.current = null;
    schedule(idleIntervalMs);
  }, [idleIntervalMs, schedule]);

  const startAction = useCallback(() => {
    activeDeadlineRef.current = Date.now() + maxActiveDurationMs;
    schedule(activeIntervalMs);
  }, [activeIntervalMs, maxActiveDurationMs, schedule]);

  const stopAction = useCallback(() => {
    if (activeDeadlineRef.current !== null) {
      startIdlePolling();
    }
  }, [startIdlePolling]);

  useEffect(() => {
    if (!enabled) {
      clearPolling();
      return;
    }
    hasFetchedOnceRef.current = false;
    runFetches();
    startIdlePolling();
    return clearPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const watchdog = setInterval(() => {
      if (activeDeadlineRef.current !== null && Date.now() > activeDeadlineRef.current) {
        startIdlePolling();
      }
    }, 1000);
    return () => clearInterval(watchdog);
  }, [enabled, startIdlePolling]);

  return { startAction, stopAction, refreshNow: runFetches };
}
